import React, { useCallback, useState } from "react";
import { Alert, Button, FieldError, Input, Label, Spinner, TextField } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { logger } from "@domain/infrastructure/logger";

interface ExternalProps {
    onSubmit: (newPassword: string) => Promise<{ redirect: boolean } | void>;
}

interface NewPasswordFormType {
    password: string;
    password_repeat: string;
}

export const NewPasswordForm = ({ onSubmit }: ExternalProps) => {
    const { formatMessage } = useIntl();
    const [password, setPassword] = useState("");
    const [passwordRepeat, setPasswordRepeat] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isFormValid = useCallback(() => {
        return !password || password.length < 6 || password !== passwordRepeat;
    }, [password, passwordRepeat]);

    const handlePasswordRepeatChange = useCallback(
        (value: string) => {
            setPasswordRepeat(value);
            if (value && value !== password) {
                setError(formatMessage({ id: "app.validation.error.passwords_not_match" }));
            } else {
                setError(null);
            }
        },
        [password, formatMessage],
    );

    const handlePasswordChange = useCallback(
        (value: string) => {
            setPassword(value);
            if (passwordRepeat && value !== passwordRepeat) {
                setError(formatMessage({ id: "app.validation.error.passwords_not_match" }));
            } else {
                setError(null);
            }
        },
        [passwordRepeat, formatMessage],
    );

    const handleSubmit = useCallback(async () => {
        if (isFormValid()) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const result = await onSubmit(password);

            if (result && result.redirect) {
                return;
            }

            setPassword("");
            setPasswordRepeat("");
        } catch (err: any) {
            logger.error({ err });
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    }, [isFormValid, onSubmit, password]);

    const resetError = useCallback(() => {
        setError(null);
    }, []);

    const onPasswordChange = useCallback(
        (v: string) => {
            handlePasswordChange(v);
            resetError();
        },
        [handlePasswordChange, resetError],
    );

    const onPasswordRepeatChange = useCallback(
        (v: string) => {
            handlePasswordRepeatChange(v);
            resetError();
        },
        [handlePasswordRepeatChange, resetError],
    );

    return (
        <div>
            {error && (
                <Alert status="danger" className="mb-4">
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {error || <FormattedMessage id="app.global.error" data-cy="error-alert" />}
                        </Alert.Title>
                    </Alert.Content>
                </Alert>
            )}
            <div className="mb-4">
                <FormattedMessage id="app.new_password.description" />
            </div>
            <div className="flex flex-col gap-4">
                <TextField isInvalid={!!password && password.length < 6} value={password} onChange={onPasswordChange}>
                    <Label>{formatMessage({ id: "app.global.password" })}</Label>
                    <Input type="password" autoComplete="new-password" />
                    <FieldError>
                        {password && password.length < 6
                            ? formatMessage({ id: "app.validation.error.short_password" }, { length: 6 })
                            : undefined}
                    </FieldError>
                </TextField>
                <TextField
                    isInvalid={!!passwordRepeat && !!password && passwordRepeat !== password}
                    value={passwordRepeat}
                    onChange={onPasswordRepeatChange}
                >
                    <Label>{formatMessage({ id: "app.global.password_repeat" })}</Label>
                    <Input type="password" autoComplete="new-password" />
                    <FieldError>
                        {passwordRepeat && password && passwordRepeat !== password
                            ? formatMessage({ id: "app.validation.error.passwords_not_match" })
                            : undefined}
                    </FieldError>
                </TextField>
                <Button
                    isDisabled={isFormValid()}
                    isPending={isSubmitting}
                    onPress={handleSubmit}
                    size="lg"
                    className="w-full"
                >
                    {({ isPending }) => (
                        <>
                            {isPending ? <Spinner color="current" size="sm" /> : null}
                            <FormattedMessage id="app.button.send" />
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};
